import { Injectable, Injector } from '@angular/core';
import { ConfigService } from './config.service';
import Amplify, { Auth, Cache } from 'aws-amplify';
import * as Sentry from "@sentry/angular";
import { DevImpersonationService } from '../_services/dev-impersonation.service';
import { DemoModeService } from '../demo-mode';
import { patchAmplifyApiForDemoMode } from '../demo-mode';
import { ViewAsScopeService, patchAmplifyApiForViewAsScope } from '../view-as-scope';

@Injectable({
  providedIn: 'root'
})
export class AppInitializationService {

  constructor(
    private configService: ConfigService,
    private demoModeService: DemoModeService,
    private viewAsScopeService: ViewAsScopeService,
    private injector: Injector
  ) {}

  /**
   * Attach Cognito plus any stored impersonation token.
   * Used only on the local and staging Amplify endpoints. The prod endpoint uses
   * createProductionHeader() and never reads sessionStorage.
   */
  private createCustomHeader(includeImpersonation: boolean = true): () => Promise<{ idtoken: string; 'X-Impersonation-Token'?: string }> {
    return async () => {
      const headers: { idtoken: string; 'X-Impersonation-Token'?: string } = {
        idtoken: `${(await Auth.currentSession()).getIdToken().getJwtToken()}`
      };

      if (includeImpersonation) {
        const impersonationToken = DevImpersonationService.getTokenFromStorage();
        if (impersonationToken) {
          headers['X-Impersonation-Token'] = impersonationToken;
        }
      }

      return headers;
    };
  }

  /**
   * Patch Cache.getItem to return View As effective attributes when enabled.
   * Production portal (host=prod) does not intercept Cache via impersonation.
   */
  private patchCacheForViewAsScope(): void {
    const originalCacheGetItem = Cache.getItem.bind(Cache);
    Cache.getItem = (key: string): unknown => {
      if (key === 'userAttr' && this.viewAsScopeService.isEnabled()) {
        return this.viewAsScopeService.getEffectiveUserAttributes();
      }
      return originalCacheGetItem(key);
    };
  }

  /**
   * Create a simple custom header function without impersonation (for production)
   */
  private createProductionHeader(): () => Promise<{ idtoken: string }> {
    return async () => {
      return {
        idtoken: `${(await Auth.currentSession()).getIdToken().getJwtToken()}`
      };
    };
  }

  initializeApp(): void {
    const config = this.configService.getAll();

    // Configure Amplify
    Amplify.configure({
      Auth: {
        region: config.region,
        userPoolId: config.userPoolId,
        userPoolWebClientId: config.clientId
      },
      API: {
        endpoints: [
          {
            name: 'local',
            endpoint: config.apiEndpoints.local,
            custom_header: this.createCustomHeader(true) // Enable impersonation for local
          },
          {
            name: 'staging',
            endpoint: config.apiEndpoints.staging,
            custom_header: this.createCustomHeader(true) // Enable impersonation for staging
          },
          {
            name: 'prod',
            endpoint: config.apiEndpoints.prod,
            custom_header: this.createProductionHeader() // No impersonation in production
          },
          {
            name: 'banner_s3_staging',
            endpoint: config.apiEndpoints.banner_s3_staging
          },
          {
            name: 'banner_s3',
            endpoint: config.apiEndpoints.banner_s3
          }
        ]
      }
    });

    // Patch Amplify API to inject View As scope params when enabled (BEFORE demo mode so we modify requests first)
    patchAmplifyApiForViewAsScope(this.viewAsScopeService);

    // Patch Amplify API to anonymize responses when demo mode is enabled (super admins only)
    patchAmplifyApiForDemoMode(this.demoModeService, this.viewAsScopeService);

    // Patch Cache.getItem to return effective user attributes when View As is enabled.
    this.patchCacheForViewAsScope();

    // Construct impersonation after config + the View As Cache patch. PortalViewGuard
    // reads Cache before BaseComponent is created, so staging production builds must
    // enable this here or the retail preview looks like super-admin on first navigation.
    this.injector.get(DevImpersonationService).enableForRuntime(config.host);

    // Initialize Sentry only in production
    if (config.host === 'prod') {
      Sentry.init({
        dsn: config.sentryDsn,
        integrations: [],
      });
    }
  }
}
